import { useEffect, useState, type RefObject } from 'react';

/**
 * Subscribe to a container element's width via ResizeObserver and return true
 * when the measured width is at or below `max`. Used by ResponsiveAppShell so
 * the narrow chrome decision tracks the container (e.g. Chrome side panel
 * iframe), not just the window — `window.matchMedia` mis-reports inside
 * iframes.
 *
 * Initial value stays `false` on first render and is corrected on the first
 * post-mount measurement. This avoids hydration mismatches if the app is ever
 * server-rendered (window-based seeding would diverge from SSR's `false`),
 * and matches the convention of `useSyncExternalStore`-style hooks.
 */
export function useContainerQuery(
  ref: RefObject<HTMLElement | null>,
  max: number
): boolean {
  const [isNarrow, setIsNarrow] = useState<boolean>(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    // Initial measurement — covers the first paint before any resize event.
    setIsNarrow(node.clientWidth <= max);

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setIsNarrow(entry.contentRect.width <= max);
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, max]);

  return isNarrow;
}
